/**
 * คิวคำสั่งออฟไลน์ของ Android handheld (M11 — ครึ่งฝั่งอุปกรณ์)
 *
 * เก็บ "คำสั่งที่ผู้ปฏิบัติงานสั่ง" ไว้ใน IndexedDB แล้วเดิน lifecycle ของแต่ละ
 * ใบตาม D-078 — ไฟล์นี้เป็นเจ้าของ **สถานะในเครื่อง** อย่างเดียว การส่งจริงอยู่ที่
 * `syncEngine.ts` ซึ่งเป็นไฟล์เดียวในโมดูลนี้ที่แตะ network
 *
 * สิ่งที่คิวนี้ **ไม่ทำ** และห้ามทำ:
 *   - ไม่คำนวณยอดคงเหลือ จำนวนที่เหลือ สถานะ lot หรือผลการ post ใดๆ
 *   - ไม่ตัดสินเองว่าคำสั่งสำเร็จ — `ACCEPTED`/`REJECTED`/`CONFLICT` เขียนได้
 *     เฉพาะค่าที่ engine คัดมาจาก response ของ server ตาม `client_event_id`
 *   - ไม่แก้ conflict และไม่ลบคำสั่งที่ถูกปฏิเสธ — นั่นเป็นงานของคนผ่านหน้าจอ
 *     conflict (WO แยก) คิวแค่ทำให้เห็นว่ามีของค้างอยู่
 *   - ไม่ตรวจ business rule ราย command_type — payload เป็นของทึบ เก็บอย่างไร
 *     คืนอย่างนั้น
 *
 * (`docs/architecture.md` Dependency Rule 8, `docs/SECURITY.md` Offline/Online
 * Policy, `docs/ERD.md` ตาราง `offline_sync_command`)
 */

import {
  buildOfflineCommandRecord,
  byDeviceSequenceNo,
  isDrainableSyncStatus,
  needsHumanAttention,
  normalizeOfflineCommandInput,
  OfflineQueueError,
  PENDING_LOCAL,
  ACCEPTED,
  SYNCING,
  UNALLOCATED_DEVICE_SEQUENCE_NO,
  type LocalSyncStatus,
  type OfflineCommandInput,
  type OfflineCommandRecord,
  type OfflineQueueErrorReason,
  type ServerSyncStatus,
} from './commandEnvelope';
import {
  clearWriteFailure,
  closeQueueConnection,
  COMMAND_STORE,
  DEVICE_SEQUENCE_INDEX,
  getQueueConnection,
  getWriteFailureReason,
  META_STORE,
  readFromStore,
  recordWriteFailure,
  SEQUENCE_COUNTER_KEY,
  toReadError,
  toWriteError,
  writeToStore,
} from './commandQueueDb';

export type QueueAvailability = 'READY' | 'UNAVAILABLE';

export interface OfflineQueueStatus {
  readonly availability: QueueAvailability;
  /**
   * จำนวนคำสั่งที่รอส่ง (`PENDING_LOCAL` + `SYNCING`) — เป็น `null` เมื่ออ่านคิวไม่ได้
   *
   * ห้ามใช้ 0 แทน "ไม่รู้" เด็ดขาด คิวที่อ่านไม่ได้ไม่ใช่คิวว่าง
   *
   * `SYNCING` ถูกนับเป็น "รอซิงค์" ด้วย เพราะในมุมของผู้ปฏิบัติงานคำสั่งใบนั้น
   * ยังไม่ได้คำตอบจาก server — การหักออกจะทำให้ตัวเลขลดลงตอนกดส่งทั้งที่ยังไม่มี
   * อะไรถูกรับเลย
   */
  readonly count: number | null;
  /** จำนวนที่ server รับแล้ว — `null` เมื่ออ่านคิวไม่ได้ */
  readonly accepted: number | null;
  /** `CONFLICT` + `REJECTED` — ต้องมีคนมาดู ไม่ใช่สิ่งที่ retry เองได้ */
  readonly needsAttention: number | null;
  readonly reason: OfflineQueueErrorReason | null;
}

/** จำนวนคำสั่งแยกตามปลายทางของ lifecycle — ใช้บอกผู้ปฏิบัติงานว่าอะไรอยู่ตรงไหน */
export interface OfflineQueueCounts {
  readonly pending: number;
  readonly accepted: number;
  readonly needsAttention: number;
}

/**
 * สร้าง `client_event_id`
 *
 * ต้องมาจากแหล่งสุ่มเชิงเข้ารหัสเท่านั้น เพราะค่านี้เป็น idempotency key ที่
 * server ใช้ dedupe — ถ้าชนกัน คำสั่งคนละใบจะถูกมองว่าเป็นใบเดียวกัน จึงยอมให้
 * ล้มเหลวดังๆ ดีกว่าถอยไปใช้ `Math.random()`
 */
export function generateClientEventId(): string {
  const webCrypto = (globalThis as { crypto?: Crypto }).crypto;

  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');
  }

  throw new OfflineQueueError(
    'NO_SECURE_RANDOM',
    'เครื่องนี้ไม่มีแหล่งสุ่มที่ปลอดภัยพอจะสร้าง client_event_id',
  );
}

/**
 * ปล่อย connection ของคิว
 *
 * การอ่าน/เขียนครั้งถัดไปจะเปิดใหม่เอง จึงใช้พิสูจน์ได้ว่าคำสั่งที่บันทึกไว้
 * อยู่รอดข้ามการปิด-เปิดฐานข้อมูล ไม่ใช่แค่ค้างอยู่ใน connection เดิม
 */
export async function closeOfflineQueue(): Promise<void> {
  await closeQueueConnection();
}

const listeners = new Set<() => void>();

/** แจ้งหน้าจอที่กำลังแสดงจำนวนคำสั่งรอซิงค์ให้อ่านค่าใหม่ */
export function subscribeToOfflineQueue(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyQueueChanged(): void {
  for (const listener of [...listeners]) {
    listener();
  }
}

/**
 * จ่าย `device_sequence_no` แล้วบันทึกแถวใน **ธุรกรรมเดียวกัน**
 *
 * counter อยู่ในฐานข้อมูลเดียวกับคิว ไม่ใช่ในหน่วยความจำหรือคนละ store
 * เพราะ counter ที่ reset ตอน reload จะสลับลำดับงานของผู้ปฏิบัติงานแบบเงียบๆ
 * และ index `by_device_sequence_no` เป็น unique จึงกันเลขซ้ำที่ระดับฐานข้อมูลอีกชั้น
 */
function allocateAndStore(
  db: IDBDatabase,
  input: OfflineCommandInput,
  clientEventId: string,
  occurredAtDevice: string,
): Promise<OfflineCommandRecord> {
  return new Promise<OfflineCommandRecord>((resolve, reject) => {
    let transaction: IDBTransaction;
    try {
      transaction = db.transaction([COMMAND_STORE, META_STORE], 'readwrite');
    } catch (cause) {
      reject(toWriteError(cause));
      return;
    }

    let settled = false;
    let record: OfflineCommandRecord | null = null;

    const fail = (cause: unknown): void => {
      if (settled) {
        return;
      }
      settled = true;
      reject(toWriteError(cause));
    };

    const commands = transaction.objectStore(COMMAND_STORE);
    const meta = transaction.objectStore(META_STORE);

    const readCounter = meta.get(SEQUENCE_COUNTER_KEY);
    readCounter.onerror = () => {
      fail(readCounter.error);
    };
    readCounter.onsuccess = () => {
      const stored = readCounter.result as unknown;
      const lastAllocated =
        typeof stored === 'number' && Number.isSafeInteger(stored)
          ? stored
          : UNALLOCATED_DEVICE_SEQUENCE_NO;
      const deviceSequenceNo = lastAllocated + 1;

      record = buildOfflineCommandRecord(input, {
        client_event_id: clientEventId,
        device_sequence_no: deviceSequenceNo,
        occurred_at_device: occurredAtDevice,
      });

      /* `add` ไม่ใช่ `put` — client_event_id ซ้ำต้องถูกปฏิเสธ ไม่ใช่ทับของเดิม */
      const insert = commands.add(record);
      insert.onerror = () => {
        fail(insert.error);
      };

      const bumpCounter = meta.put(deviceSequenceNo, SEQUENCE_COUNTER_KEY);
      bumpCounter.onerror = () => {
        fail(bumpCounter.error);
      };
    };

    transaction.oncomplete = () => {
      if (settled) {
        return;
      }
      if (record === null) {
        settled = true;
        reject(toWriteError(new Error('ธุรกรรมจบโดยไม่ได้บันทึกคำสั่ง')));
        return;
      }
      settled = true;
      resolve(record);
    };
    transaction.onabort = () => {
      fail(transaction.error);
    };
    transaction.onerror = () => {
      fail(transaction.error);
    };
  });
}

/**
 * บันทึกคำสั่งหนึ่งใบลงคิว
 *
 * สำเร็จแปลว่า "เก็บไว้ในเครื่องแล้ว" เท่านั้น **ไม่ใช่** server รับแล้ว
 * ผู้เรียกห้ามแสดงผลว่าบันทึก/โพสต์สำเร็จ
 */
export async function enqueueOfflineCommand(
  input: OfflineCommandInput,
): Promise<OfflineCommandRecord> {
  /* ตรวจรูปแบบและ credential guard ให้จบก่อนเปิดธุรกรรม */
  const normalized = normalizeOfflineCommandInput(input);
  const clientEventId = generateClientEventId();
  const occurredAtDevice = new Date().toISOString();

  try {
    const db = await getQueueConnection();
    const record = await allocateAndStore(db, normalized, clientEventId, occurredAtDevice);
    clearWriteFailure();
    notifyQueueChanged();
    return record;
  } catch (error) {
    const queueError = toWriteError(error);
    recordWriteFailure(queueError.reason);
    notifyQueueChanged();
    throw queueError;
  }
}

/**
 * อ่านคำสั่งทั้งคิวเรียงตาม `device_sequence_no`
 *
 * อ่านผ่าน index แล้วยังเรียงซ้ำอีกครั้งโดยตั้งใจ เพื่อให้ลำดับผูกกับตัวเลข
 * ไม่ใช่กับพฤติกรรมการวนคีย์ของ IndexedDB
 */
export async function readOfflineCommands(): Promise<OfflineCommandRecord[]> {
  const db = await getQueueConnection();
  const rows = await readFromStore<OfflineCommandRecord[]>(db, COMMAND_STORE, (store) =>
    store.index(DEVICE_SEQUENCE_INDEX).getAll(),
  );
  return [...rows].sort(byDeviceSequenceNo);
}

/**
 * คำสั่งที่ยังต้องส่ง เรียงตาม `device_sequence_no` (D-078)
 *
 * รวม `SYNCING` ที่ค้างจาก drain รอบก่อนที่ขาดตอนไว้ด้วยเสมอ — ส่งซ้ำปลอดภัย
 * เพราะ server dedupe ด้วย `client_event_id` แต่คำสั่งที่ตกหล่นไม่มีใครเอากลับมาได้
 *
 * กรองสถานะในหน่วยความจำแทนการเพิ่ม index ใหม่โดยเจตนา: index เพิ่มเมื่อไหร่
 * ต้องขึ้น `OFFLINE_QUEUE_DB_VERSION` ซึ่งแปลว่าเครื่องหน้างานที่เปิดสองแท็บอยู่จะ
 * เจอ `UPGRADE_BLOCKED` กลางกะ — แลกกับการวนรายการของคิวหนึ่งกะซึ่งเล็กมาก
 */
export async function readDrainableCommands(): Promise<OfflineCommandRecord[]> {
  const rows = await readOfflineCommands();
  return rows.filter((row) => isDrainableSyncStatus(row.sync_status));
}

/**
 * นับคำสั่งแยกตามปลายทาง — อ่านรอบเดียวเพื่อให้สามตัวเลขมาจากภาพเดียวกันของคิว
 */
export async function readOfflineQueueCounts(): Promise<OfflineQueueCounts> {
  const rows = await readOfflineCommands();
  let pending = 0;
  let accepted = 0;
  let needsAttention = 0;

  for (const row of rows) {
    if (isDrainableSyncStatus(row.sync_status)) {
      pending += 1;
    } else if (row.sync_status === ACCEPTED) {
      accepted += 1;
    } else if (needsHumanAttention(row.sync_status)) {
      needsAttention += 1;
    }
  }

  return { pending, accepted, needsAttention };
}

export async function readPendingCommandCount(): Promise<number> {
  return (await readOfflineQueueCounts()).pending;
}

/**
 * เปลี่ยนสถานะของหลายคำสั่งในธุรกรรมเดียว
 *
 * `allowedFrom` เป็นด่านที่ทำให้การเปลี่ยนสถานะเป็น **transition** ไม่ใช่การเขียนทับ:
 * แถวที่สถานะปัจจุบันไม่เข้าเงื่อนไข (เช่น server ตอบผลของคำสั่งที่ถูก mark เป็น
 * terminal ไปแล้ว) จะถูกข้าม ไม่ใช่ถูกทับ — และทั้งชุดอยู่ในธุรกรรมเดียว คิวจึงไม่มี
 * สภาพครึ่ง ๆ ถ้าแอปถูกปิดกลางคัน
 *
 * คืนจำนวนแถวที่เปลี่ยนจริง เพื่อให้ผู้เรียกรู้ว่าอะไรถูกข้ามไปบ้าง
 */
async function transitionCommands(
  nextStatusOf: ReadonlyMap<string, LocalSyncStatus>,
  allowedFrom: (current: unknown) => boolean,
): Promise<number> {
  if (nextStatusOf.size === 0) {
    return 0;
  }

  try {
    const db = await getQueueConnection();
    const changed = await writeToStore<number>(db, COMMAND_STORE, (store, fail) => {
      let updated = 0;

      for (const [clientEventId, nextStatus] of nextStatusOf) {
        const read = store.get(clientEventId);
        read.onerror = () => {
          fail(read.error);
        };
        read.onsuccess = () => {
          const record = read.result as OfflineCommandRecord | undefined;
          if (record === undefined || !allowedFrom(record.sync_status)) {
            return;
          }
          if (record.sync_status === nextStatus) {
            return;
          }

          const write = store.put({ ...record, sync_status: nextStatus });
          write.onerror = () => {
            fail(write.error);
          };
          write.onsuccess = () => {
            updated += 1;
          };
        };
      }

      return () => updated;
    });

    clearWriteFailure();
    notifyQueueChanged();
    return changed;
  } catch (error) {
    const queueError = toWriteError(error);
    recordWriteFailure(queueError.reason);
    notifyQueueChanged();
    throw queueError;
  }
}

function statusMap(
  clientEventIds: readonly string[],
  status: LocalSyncStatus,
): Map<string, LocalSyncStatus> {
  return new Map(clientEventIds.map((clientEventId) => [clientEventId, status]));
}

/**
 * จองคำสั่งชุดหนึ่งเข้า batch ที่กำลังจะส่ง (`PENDING_LOCAL → SYNCING`)
 *
 * ทำก่อนยิง request เสมอ เพื่อให้ drain ที่ทับกันหรือรอบถัดไปมองเห็นว่าใบเหล่านี้
 * มีคนถืออยู่ — และถ้า response หายไปกลางทาง สถานะที่ค้างอยู่คือร่องรอยที่บอกว่า
 * "เคยส่งไปแล้วครั้งหนึ่ง" ไม่ใช่คำสั่งที่หายไปเฉย ๆ
 */
export async function markCommandsSyncing(clientEventIds: readonly string[]): Promise<number> {
  return transitionCommands(statusMap(clientEventIds, SYNCING), isDrainableSyncStatus);
}

/**
 * คืนคำสั่งกลับเข้าคิว (`SYNCING → PENDING_LOCAL`) เมื่อส่งไม่ถึงหรือ server ไม่ได้
 * ตัดสินให้
 *
 * ใช้กับความล้มเหลวของ transport เท่านั้น — ความล้มเหลวแบบนี้ **ไม่ใช่คำตอบ** ของ
 * server จึงห้ามกลายเป็นสถานะปลายทาง คำสั่งต้องกลับไปรอรอบหน้าเสมอ
 */
export async function revertSyncingToPending(clientEventIds: readonly string[]): Promise<number> {
  return transitionCommands(
    statusMap(clientEventIds, PENDING_LOCAL),
    (current) => current === SYNCING,
  );
}

/**
 * คัดผลรายคำสั่งจาก response ลงแถวตาม `client_event_id` (D-078)
 *
 * เขียนได้เฉพาะแถวที่ยังอยู่ในสถานะระหว่างทาง — ผลของ server ไม่ทับสถานะปลายทาง
 * ที่เคยบันทึกไว้แล้ว และ `client_event_id` ที่ไม่มีในคิวจะถูกข้ามเงียบ ๆ (คิวนี้
 * ไม่ใช่ที่เก็บผลของอุปกรณ์เครื่องอื่น)
 */
export async function applyServerSyncStatuses(
  statuses: ReadonlyMap<string, ServerSyncStatus>,
): Promise<number> {
  return transitionCommands(new Map(statuses), isDrainableSyncStatus);
}

/** เลขลำดับล่าสุดที่เคยจ่ายไป — ใช้พิสูจน์ว่าไม่ reset หลังเปิดใหม่ */
export async function readLastAllocatedDeviceSequenceNo(): Promise<number> {
  const db = await getQueueConnection();
  const stored = await readFromStore<unknown>(db, META_STORE, (store) =>
    store.get(SEQUENCE_COUNTER_KEY),
  );
  return typeof stored === 'number' && Number.isSafeInteger(stored)
    ? stored
    : UNALLOCATED_DEVICE_SEQUENCE_NO;
}

/**
 * สถานะคิวสำหรับหน้าจอ
 *
 * ล้มเหลวทุกแบบกลายเป็น `UNAVAILABLE` พร้อมเหตุผล — ไม่มีทางที่หน้าจอจะได้
 * `count: 0` จากคิวที่อ่านไม่ได้
 */
export async function readOfflineQueueStatus(): Promise<OfflineQueueStatus> {
  let counts: OfflineQueueCounts;
  try {
    counts = await readOfflineQueueCounts();
  } catch (error) {
    const queueError = toReadError(error);
    return {
      availability: 'UNAVAILABLE',
      count: null,
      accepted: null,
      needsAttention: null,
      reason: queueError.reason,
    };
  }

  /*
   * อ่านได้แต่เขียนไม่ได้ก็ยังถือว่าใช้งานไม่ได้ — ไม่รายงานจำนวนออกไป เพราะ
   * ตัวเลขที่ดูปกติจะทำให้ผู้ปฏิบัติงานสแกนต่อทั้งที่คำสั่งใหม่ตกหล่น
   */
  const writeFailure = getWriteFailureReason();
  if (writeFailure !== null) {
    return {
      availability: 'UNAVAILABLE',
      count: null,
      accepted: null,
      needsAttention: null,
      reason: writeFailure,
    };
  }

  return {
    availability: 'READY',
    count: counts.pending,
    accepted: counts.accepted,
    needsAttention: counts.needsAttention,
    reason: null,
  };
}
